import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { downloadCsv } from "@/lib/utils";
import {
  cancelAttendanceConfirmation,
  getAttendanceConfirmationCopy,
  openAttendanceConfirmation,
} from "@/lib/attendance-confirmation";
import { resolveLiffSession } from "@/lib/liff-session";
import { toast } from "sonner";
import {
  AttendanceLocation,
  EMPLOYEE_LIFF_ID,
  EXECUTIVE_LIFF_ID,
  ModuleType,
  TabType,
  ensureLiffSdk,
  executiveEntry,
  liffId,
  moduleToTab,
  isModuleAllowed,
  parseSystemFlags,
  readDirectEntry,
  thaiTime,
  thaiTimeShort,
  workDuration,
} from "@/components/home/types";
import { HomeHeader } from "@/components/home/HomeHeader";
import { AttendanceTab } from "@/components/home/AttendanceTab";
import { MyPulseTab } from "@/components/home/MyPulseTab";
import { LeaveTab } from "@/components/home/LeaveTab";
import { LeaveApprovalsTab } from "@/components/home/LeaveApprovalsTab";
import { LiveBoardTab } from "@/components/home/LiveBoardTab";
import { PayrollTab } from "@/components/home/PayrollTab";
import { TeamDirectoryTab } from "@/components/home/TeamDirectoryTab";
import { HrSettingsTab } from "@/components/home/HrSettingsTab";
import { SalaryAdvanceTab } from "@/components/home/SalaryAdvanceTab";
import { AdvanceApprovalsTab } from "@/components/home/AdvanceApprovalsTab";
import { TaskDispatchTab } from "@/components/home/TaskDispatchTab";
import { WelfareTab } from "@/components/home/WelfareTab";
import { HomeModals } from "@/components/home/HomeModals";
import { useLanguage, Language } from "@/lib/i18n";

declare global {
  interface Window {
    liff?: {
      init: (options: { liffId: string }) => Promise<void>;
      isLoggedIn: () => boolean;
      login: () => void;
      logout: () => void;
      getIDToken: () => string | null;
    };
  }
}

export default function Home() {
  const { language, setLanguage } = useLanguage();
  const initialEntry = useMemo(readDirectEntry, []);
  const [activeTab, setActiveTab] = useState<TabType>(() =>
    initialEntry.module
      ? moduleToTab[initialEntry.module]
      : executiveEntry
      ? "live-board"
      : "attendance"
  );
  const [query, setQuery] = useState("");
  const [idToken, setIdToken] = useState<string>();
  const [liffState, setLiffState] = useState<"loading" | "ready" | "login" | "error">("loading");
  const [liffError, setLiffError] = useState("");
  const [confirmAction, setConfirmAction] = useState<"checkin" | "checkout" | null>(null);
  const [entryModule, setEntryModule] = useState<ModuleType | null>(initialEntry.module);
  const [entryAction, setEntryAction] = useState<"checkin" | "checkout" | null>(initialEntry.action);
  const [checkInSuccess, setCheckInSuccess] = useState(false);
  const [lateReasonRequest, setLateReasonRequest] = useState<{ attendanceId: number; lateMinutes: number } | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [sheetMonth, setSheetMonth] = useState(() =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Bangkok",
      year: "numeric",
      month: "2-digit",
    }).format(new Date())
  );
  const [selectedLocation, setSelectedLocation] = useState<AttendanceLocation | null>(null);
  const [geoPermission, setGeoPermission] = useState<PermissionState | "unsupported" | "unknown">("unknown");

  const [savedName, setSavedName] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("demo_employee_saved_name") || "";
    }
    return "";
  });
  const [inputName, setInputName] = useState("");
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);
  const [leaveDraft, setLeaveDraft] = useState({
    leaveType: "vacation" as "vacation" | "sick" | "business",
    startDate: "",
    endDate: "",
    reason: "",
    handoverTo: "",
    handoverDetails: "",
  });

  const [settingsDraft, setSettingsDraft] = useState({
    payrollPeriodStart: "01",
    payrollPeriodEnd: "last",
    standardStart: "09:00",
    standardEnd: "18:00",
    latenessGraceMinutes: 15,
    standardWorkHours: 8,
    payableWorkdays: 26,
    otMultiplier: 150,
  });

  const [salaryDraft, setSalaryDraft] = useState({
    displayName: "",
    monthlySalary: "",
    dailyRate: "",
    hourlyRate: "",
    effectiveFrom: "",
  });

  const [employeeDraft, setEmployeeDraft] = useState<{
    id?: number;
    displayName: string;
    employeeCode: string;
    department: string;
  }>({ displayName: "", employeeCode: "", department: "" });
  const [performanceDraft, setPerformanceDraft] = useState({
    lineUserId: "",
    displayName: "",
    reviewPeriod: `${new Date().getFullYear()}-H2`,
    qualityScore: 3,
    productivityScore: 3,
    teamworkScore: 3,
    attendanceScore: 3,
    managerFeedback: "",
  });
  const [performanceComments, setPerformanceComments] = useState<Record<number, string>>({});

  const systemConfigs = trpc.systemConfig.get.useQuery(undefined, {
    staleTime: 30000,
  });
  const updateSystemConfig = trpc.systemConfig.update.useMutation({
    onSuccess: () => {
      toast.success("อัปเดตการตั้งค่าฟีเจอร์ระบบสำเร็จ");
      void systemConfigs.refetch();
    },
    onError: (e) => toast.error(e.message),
  });
  const featureFlags = parseSystemFlags(systemConfigs.data);

  const employees = trpc.employees.list.useQuery(undefined, {
    retry: 1,
    staleTime: 60_000,
  });
  const identity = trpc.identity.status.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken), retry: 0 }
  );
  const identityCandidates = trpc.identity.candidates.useQuery(
    { idToken: idToken ?? "" },
    {
      enabled: Boolean(idToken && !identity.data?.binding),
      retry: 0,
      staleTime: 300000,
    }
  );
  const bindIdentity = trpc.identity.bind.useMutation({
    onSuccess: () => {
      void identity.refetch();
      void status.refetch();
      toast.success("ผูกบัญชี LINE กับพนักงานเรียบร้อย");
    },
    onError: (e) => toast.error(e.message),
  });
  const status = trpc.attendance.status.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken), retry: 0 }
  );
  const welcome = trpc.attendance.welcome.useMutation();
  const myMonthlySummary = trpc.attendance.myMonthlySummary.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && status.data?.profile), retry: 0 }
  );
  const leaveDashboard = trpc.leave.dashboard.useQuery(
    { idToken: idToken ?? "" },
    {
      enabled: false,
      retry: 0,
    }
  );
  const myPerformance = trpc.performance.mine.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && status.data?.profile), retry: 0 }
  );
  const commentPerformance = trpc.performance.comment.useMutation({
    onSuccess: () => {
      toast.success("ส่งความคิดเห็นต่อผลประเมินแล้ว");
      void myPerformance.refetch();
    },
    onError: (e) => toast.error(e.message),
  });
  const createLeave = trpc.leave.create.useMutation({
    onSuccess: () => {
      toast.success("ส่งคำขอวันลาเรียบร้อย");
      setLeaveDraft({
        leaveType: "vacation",
        startDate: "",
        endDate: "",
        reason: "",
        handoverTo: "",
        handoverDetails: "",
      });
      void leaveDashboard.refetch();
    },
    onError: (e) => toast.error(e.message),
  });
  const cancelLeave = trpc.leave.cancel.useMutation({
    onSuccess: () => void leaveDashboard.refetch(),
    onError: (e) => toast.error(e.message),
  });

  const checkIn = trpc.attendance.checkIn.useMutation({
    onSuccess: (result) => {
      void status.refetch();
      void myMonthlySummary.refetch();
      if (result.requiresLateReason && result.record?.id) {
        setLateReasonRequest({ attendanceId: result.record.id, lateMinutes: result.lateMinutes ?? 0 });
      }
      if (result.status === "checked_in") {
        setCheckInSuccess(true);
        toast.success("บันทึกเวลาเช็คอินสำเร็จเรียบร้อย");
        window.setTimeout(() => {
          setCheckInSuccess(false);
        }, 2200);
        if (result.notificationSent === false) {
          toast.warning("บันทึกเวลาแล้ว แต่ส่งการแจ้งเตือน LINE ไม่สำเร็จ");
        }
      } else if (result.status === "already_checked_in") {
        toast.info("วันนี้คุณได้บันทึกเวลาเช็คอินแล้ว");
      } else if (result.status === "resynced") {
        toast.success("ซิงก์ข้อมูลเช็คอินกับ Google Sheet สำเร็จแล้ว");
      } else if (result.status === "sync_failed") {
        toast.warning("บันทึกเวลาแล้ว แต่ซิงก์ Google Sheet ไม่สำเร็จ");
        if (result.notificationSent === false) {
          toast.warning("ส่งการแจ้งเตือน LINE ไม่สำเร็จ กรุณาแจ้ง HR");
        }
      }
    },
    onError: (err) => {
      toast.error(err.message || "บันทึกเวลาเช็คอินไม่สำเร็จ");
    },
  });

  const submitLateReason = trpc.attendance.submitLateReason.useMutation({
    onSuccess: () => {
      setLateReasonRequest(null);
      void status.refetch();
      toast.success(language === "my" ? "နောက်ကျရခြင်းအကြောင်း တင်ပြပြီးပါပြီ" : "ส่งเหตุผลการมาสายให้แอดมินแล้ว");
    },
    onError: (error) => toast.error(error.message),
  });

  const checkout = trpc.attendance.checkout.useMutation({
    onSuccess: (result) => {
      void status.refetch();
      void myMonthlySummary.refetch();
      if (result.status === "checked_out") {
        toast.success("บันทึกเวลาเช็คเอาท์สำเร็จเรียบร้อย");
        if (result.notificationSent === false) {
          toast.warning("บันทึกเวลาแล้ว แต่ส่งการแจ้งเตือน LINE ไม่สำเร็จ");
        }
      } else if (result.status === "already_checked_out") {
        toast.info("วันนี้คุณได้เช็คเอาท์เรียบร้อยแล้ว");
      } else if (result.status === "checkout_sync_failed") {
        toast.warning("บันทึกเวลาแล้ว แต่ซิงก์ Google Sheet ไม่สำเร็จ");
        if (result.notificationSent === false) {
          toast.warning("ส่งการแจ้งเตือน LINE ไม่สำเร็จ กรุณาแจ้ง HR");
        }
      }
    },
    onError: (err) => {
      toast.error(err.message || "เช็คเอาท์ไม่สำเร็จ");
    },
  });

  const isManager = status.data?.isManager === true;

  const leaveAdmin = trpc.leave.adminList.useQuery(
    { idToken: idToken ?? "" },
    { enabled: false, retry: 0 }
  );
  const performanceAdmin = trpc.performance.adminList.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && executiveEntry && isManager), retry: 0 }
  );
  const savePerformance = trpc.performance.save.useMutation({
    onSuccess: () => {
      toast.success("บันทึกและเผยแพร่ผลประเมินแล้ว");
      setPerformanceDraft({ ...performanceDraft, managerFeedback: "" });
      void performanceAdmin.refetch();
      void myPerformance.refetch();
    },
    onError: (e) => toast.error(e.message),
  });
  const decideLeave = trpc.leave.decide.useMutation({
    onSuccess: () => {
      toast.success("บันทึกผลอนุมัติแล้ว");
      void leaveAdmin.refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const dashboard = trpc.attendance.dashboard.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && executiveEntry && isManager), retry: 0 }
  );
  const sheetDashboard = trpc.sheetDashboard.month.useQuery(
    { idToken: idToken ?? "", month: sheetMonth },
    {
      enabled: Boolean(idToken && executiveEntry && isManager && sheetMonth >= "2026-08"),
      retry: 0,
    }
  );
  const hiddenEmployees = trpc.hiddenEmployees.list.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && executiveEntry && isManager), retry: 0 }
  );
  const hideEmployee = trpc.hiddenEmployees.hide.useMutation({
    onSuccess: () => {
      toast.success("ซ่อนรายชื่อสำเร็จ");
      void hiddenEmployees.refetch();
      void employees.refetch();
    },
  });
  const restoreEmployee = trpc.hiddenEmployees.restore.useMutation({
    onSuccess: () => {
      toast.success("นำรายชื่อกลับมาแสดงสำเร็จ");
      void hiddenEmployees.refetch();
      void employees.refetch();
      void status.refetch();
    },
  });

  const exportAttendance = trpc.attendance.export.useQuery(
    { idToken: idToken ?? "" },
    { enabled: false, retry: 0 }
  );
  const payrollSettings = trpc.payroll.settings.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && executiveEntry && isManager), retry: 0 }
  );
  const payrollProfiles = trpc.payroll.profiles.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && executiveEntry && isManager), retry: 0 }
  );
  const payrollSummary = trpc.payroll.summary.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && executiveEntry && isManager), retry: 0 }
  );

  const savePayrollSettings = trpc.payroll.saveSettings.useMutation({
    onSuccess: () => {
      toast.success("บันทึกการตั้งค่าเงินเดือนและ OT สำเร็จ");
      void payrollSettings.refetch();
      void payrollSummary.refetch();
    },
  });

  const saveSalaryProfile = trpc.payroll.saveProfile.useMutation({
    onSuccess: () => {
      toast.success("บันทึกอัตราค่าจ้างรายบุคคลสำเร็จ");
      setSalaryDraft({
        displayName: "",
        monthlySalary: "",
        dailyRate: "",
        hourlyRate: "",
        effectiveFrom: "",
      });
      void payrollProfiles.refetch();
      void payrollSummary.refetch();
    },
  });

  const managedEmployees = trpc.managedEmployees.list.useQuery(
    { idToken: idToken ?? "" },
    { enabled: Boolean(idToken && executiveEntry && isManager), retry: 0 }
  );
  const saveManagedEmployee = trpc.managedEmployees.save.useMutation({
    onSuccess: () => {
      toast.success("บันทึกข้อมูลพนักงานสำเร็จ");
      setEmployeeDraft({ displayName: "", employeeCode: "", department: "" });
      void managedEmployees.refetch();
      void employees.refetch();
    },
  });
  const deleteManagedEmployee = trpc.managedEmployees.delete.useMutation({
    onSuccess: () => {
      toast.success("ลบข้อมูลพนักงานเรียบร้อย");
      void managedEmployees.refetch();
      void employees.refetch();
      void status.refetch();
    },
  });
  const setManagedEmployeeActive = trpc.managedEmployees.setActive.useMutation({
    onSuccess: () => {
      toast.success("อัปเดตสถานะการใช้งานพนักงานเรียบร้อย");
      void managedEmployees.refetch();
      void employees.refetch();
      void status.refetch();
    },
  });

  useEffect(() => {
    if (payrollSettings.data) {
      setSettingsDraft({
        payrollPeriodStart: payrollSettings.data.payrollPeriodStart,
        payrollPeriodEnd: payrollSettings.data.payrollPeriodEnd,
        standardStart: payrollSettings.data.standardStart,
        standardEnd: payrollSettings.data.standardEnd,
        latenessGraceMinutes: payrollSettings.data.latenessGraceMinutes,
        standardWorkHours: payrollSettings.data.standardWorkHours,
        payableWorkdays: payrollSettings.data.payableWorkdays,
        otMultiplier: payrollSettings.data.otMultiplier,
      });
    }
  }, [payrollSettings.data]);

  useEffect(() => {
    const url = new URL(window.location.href);
    let tabParam = url.searchParams.get("tab") as TabType | null;
    let actionParam = url.searchParams.get("action");
    let moduleParam = url.searchParams.get("module") as ModuleType | null;
    let sectionParam = url.searchParams.get("section");

    const liffStateParam = url.searchParams.get("liff.state");
    if (liffStateParam) {
      try {
        const decoded = decodeURIComponent(liffStateParam);
        const nestedParams = new URLSearchParams(
          decoded.startsWith("?") ? decoded : `?${decoded}`
        );
        if (!tabParam) tabParam = nestedParams.get("tab") as TabType | null;
        if (!actionParam) actionParam = nestedParams.get("action");
        if (!moduleParam) moduleParam = nestedParams.get("module") as ModuleType | null;
        if (!sectionParam) sectionParam = nestedParams.get("section");
      } catch {
        // ignore
      }
    }

    const tabAllowed = Boolean(
      tabParam &&
        (tabParam === "admin-team"
          ? executiveEntry
          : Object.entries(moduleToTab).some(
              ([module, tab]) =>
                tab === tabParam && isModuleAllowed(module as ModuleType, executiveEntry)
            ))
    );
    if (tabParam && tabAllowed) {
      if (tabParam === "admin-team") {
        setActiveTab(sectionParam === "team-directory" ? "team-directory" : "hr-settings");
      } else {
        setActiveTab(tabParam);
      }
    } else if (executiveEntry) {
      setActiveTab("live-board");
    }

    if (moduleParam && moduleParam in moduleToTab && isModuleAllowed(moduleParam, executiveEntry)) {
      setEntryModule(moduleParam);
      setActiveTab(moduleToTab[moduleParam]);
    }
    if (actionParam === "checkin" || actionParam === "checkout") setEntryAction(actionParam);
  }, [isManager, executiveEntry]);

  useEffect(() => {
    if (!idToken || !status.data?.profile || executiveEntry || isManager) return;
    const key = `demo_welcome_sent_${status.data.profile.lineUserId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "pending");
    welcome.mutate(
      { idToken },
      {
        onSuccess: (result) => {
          if (result.sent) sessionStorage.setItem(key, "sent");
          else sessionStorage.removeItem(key);
        },
        onError: () => sessionStorage.removeItem(key),
      }
    );
  }, [idToken, status.data?.profile?.lineUserId, executiveEntry, isManager]);

  useEffect(() => {
    if (
      idToken &&
      savedName &&
      !identity.isLoading &&
      !identity.data?.binding &&
      !bindIdentity.isPending
    )
      bindIdentity.mutate({ idToken, displayName: savedName, language });
  }, [idToken, savedName, identity.isLoading, identity.data?.binding, language]);

  useEffect(() => {
    if (identity.data?.binding?.language && (identity.data.binding.language === "th" || identity.data.binding.language === "my")) {
      const dbLang = identity.data.binding.language as Language;
      setLanguage(dbLang);
    }
  }, [identity.data?.binding?.language]);

  useEffect(() => {
    if (!entryAction || activeTab !== "attendance" || !status.data?.profile) return;
    const key = `demo_deep_action_${entryAction}_${window.location.href}`;
    if (sessionStorage.getItem(key)) return;
    const allowed =
      entryAction === "checkin"
        ? !status.data.today
        : Boolean(status.data.today && !status.data.today.checkedOutAt);
    if (!allowed) return;
    sessionStorage.setItem(key, "opened");
    setConfirmAction(entryAction);
  }, [entryAction, activeTab, status.data?.profile, status.data?.today]);

  useEffect(() => {
    if (
      !executiveEntry &&
      [
        "leave-approvals",
        "advance-approvals",
        "live-board",
        "payroll",
        "team-directory",
        "hr-settings",
        "admin-team",
      ].includes(activeTab)
    ) {
      setActiveTab("attendance");
    }
    if (
      executiveEntry &&
      (activeTab === "attendance" || activeTab === "my-pulse" || activeTab === "salary-advance")
    ) {
      setActiveTab("live-board");
    }
  }, [isManager, executiveEntry, activeTab]);

  useEffect(() => {
    if (!featureFlags.enableSalaryAdvance && activeTab === "advance-approvals") {
      window.history.replaceState({}, "", "/admin/?module=live-board");
      setActiveTab("live-board");
    }
    if (!featureFlags.enableSalaryAdvance && activeTab === "salary-advance") {
      setActiveTab("attendance");
    }
  }, [featureFlags.enableSalaryAdvance, activeTab]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleHistoryNavigation = () => {
      const direct = readDirectEntry();
      setEntryModule(direct.module);
      setEntryAction(direct.action);
      setActiveTab(direct.module ? moduleToTab[direct.module] : executiveEntry ? "live-board" : "attendance");
    };
    window.addEventListener("popstate", handleHistoryNavigation);
    return () => window.removeEventListener("popstate", handleHistoryNavigation);
  }, []);

  useEffect(() => {
    let active = true;
    async function init() {
      try {
        if (!liffId) throw new Error("ยังไม่ได้ตั้งค่า LIFF ID");
        await ensureLiffSdk();
        const liff = window.liff;
        if (!liff) throw new Error("ไม่พบ LINE LIFF SDK หลังโหลดเสร็จ");
        await liff.init({ liffId });
        const session = resolveLiffSession(liff.isLoggedIn(), liff.getIDToken());
        if (session.state === "login") {
          if (active) setLiffState("login");
          liff.login();
          return;
        }
        if (active) {
          setIdToken(session.idToken);
          setLiffState("ready");
        }
      } catch (error) {
        if (active) {
          setLiffError(
            error instanceof Error ? error.message : "เชื่อมต่อ LINE LIFF ไม่สำเร็จ"
          );
          setLiffState("error");
        }
      }
    }
    void init();
    return () => {
      active = false;
    };
  }, []);

  const getSilentCoordinates = async (): Promise<{
    lat?: number;
    lng?: number;
    accuracy?: number;
  }> => {
    if (typeof window === "undefined" || !navigator.geolocation) return {};
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        (error) => {
          setGeoPermission(error.code === error.PERMISSION_DENIED ? "denied" : "prompt");
          resolve({});
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
      );
    });
  };

  useEffect(() => {
    if (typeof navigator === "undefined" || !("permissions" in navigator)) {
      setGeoPermission("unsupported");
      return;
    }
    let permission: PermissionStatus | undefined;
    const update = () => setGeoPermission(permission?.state ?? "unknown");
    navigator.permissions
      .query({ name: "geolocation" })
      .then((result) => {
        permission = result;
        update();
        result.addEventListener("change", update);
      })
      .catch(() => setGeoPermission("unsupported"));
    return () => permission?.removeEventListener("change", update);
  }, []);

  const requestLocationConsentOnce = () => {
    if (typeof window !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {
          localStorage.setItem("demo_location_consented", "true");
          setGeoPermission("granted");
        },
        () => {
          localStorage.setItem("demo_location_consented", "denied");
          setGeoPermission("denied");
        },
        { timeout: 5000 }
      );
    }
  };

  const handleSaveFirstTimeName = (nameToSave: string) => {
    const trimmed = nameToSave.trim();
    if (!trimmed) {
      toast.error("กรุณาระบุชื่อ-นามสกุล");
      return;
    }
    localStorage.setItem("demo_employee_saved_name", trimmed);
    setSavedName(trimmed);
    if (idToken) bindIdentity.mutate({ idToken, displayName: trimmed, language });
    setShowFirstTimeModal(false);
    requestLocationConsentOnce();
    toast.success(`ระบบจดจำพนักงาน "${trimmed}" เรียบร้อยแล้ว`);

    if (window.liff && !window.liff.isLoggedIn()) {
      window.liff.login();
    }
  };

  const handleClearSavedUser = () => {
    if (window.confirm("ต้องการเปลี่ยนผู้ใช้งาน หรือลบข้อมูลชื่อที่จำไว้หรือไม่?")) {
      localStorage.removeItem("demo_employee_saved_name");
      setSavedName("");
      toast.info("ล้างข้อมูลผู้ใช้งานเรียบร้อย");
      if (window.liff && window.liff.isLoggedIn()) {
        try {
          window.liff.logout();
          window.location.reload();
        } catch {
          // ignore
        }
      }
    }
  };

  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("th-TH");
    return (employees.data?.names ?? []).filter(
      (name) => !term || name.toLocaleLowerCase("th-TH").includes(term)
    );
  }, [employees.data?.names, query]);

  const candidateNames = useMemo(() => {
    return identityCandidates.data?.names ?? employees.data?.names ?? [];
  }, [employees.data?.names, identityCandidates.data?.names]);

  const today = status.data?.today;
  const currentDisplayName =
    identity.data?.binding?.displayName ||
    (executiveEntry ? status.data?.profile?.displayName : "") ||
    savedName;
  const canCheckIn = Boolean(idToken || savedName) && !today && !checkIn.isPending;
  const canCheckout =
    Boolean(idToken || savedName) && Boolean(today && !today.checkedOutAt) && !checkout.isPending;
  const confirmCopy = confirmAction ? getAttendanceConfirmationCopy(confirmAction) : null;

  const visibleSourceNames =
    hiddenEmployees.data?.sourceNames.names.filter(
      (name: string) =>
        !hiddenEmployees.data.items.some((item: any) => item.displayName === name)
    ) ?? [];

  const runConfirmedAction = async () => {
    if (!idToken && !savedName) {
      toast.error("กรุณาระบุชื่อพนักงานก่อนบันทึกเวลา");
      return;
    }
    const currentAction = confirmAction;
    setConfirmAction(null);

    const gpsRequired = featureFlags.enableGPSLocation !== false;
    const geo = gpsRequired ? await getSilentCoordinates() : {};
    if (gpsRequired && (geo.lat == null || geo.lng == null || geo.accuracy == null)) {
      toast.error(
        geoPermission === "denied"
          ? "อุปกรณ์ปิดสิทธิ์ตำแหน่ง กรุณาเปิด Location ในการตั้งค่าเครื่อง/เบราว์เซอร์ แล้วลองใหม่"
          : "ไม่สามารถอ่านพิกัดปัจจุบันได้ กรุณาเปิด GPS และลองใหม่ในพื้นที่รับสัญญาณ"
      );
      localStorage.setItem("demo_location_consented", "denied");
      return;
    }

    if (currentAction === "checkin") {
      if (idToken) {
        checkIn.mutate({
          idToken,
          ...(geo.lat != null ? { lat: geo.lat, lng: geo.lng!, accuracy: geo.accuracy! } : {}),
        });
      } else {
        toast.error("กรุณาเข้าสู่ระบบผ่าน LINE เพื่อบันทึกเวลา");
      }
    } else if (currentAction === "checkout") {
      if (idToken) {
        checkout.mutate({
          idToken,
          ...(geo.lat != null ? { lat: geo.lat, lng: geo.lng!, accuracy: geo.accuracy! } : {}),
        });
      } else {
        toast.error("กรุณาเข้าสู่ระบบผ่าน LINE เพื่อบันทึกเวลา");
      }
    }
  };

  const downloadExcel = async () => {
    const result = await exportAttendance.refetch();
    if (!result.data) return;
    const rows = result.data.rows.map((row) => ({
      วันที่: row.workDate,
      "LINE User ID": row.lineUserId,
      ชื่อพนักงาน: row.displayName,
      เวลาเข้า: thaiTime(row.checkedInAt),
      เวลาออก: row.checkedOutAt ? thaiTime(row.checkedOutAt) : "ยังไม่เช็คเอาท์",
      ระยะเวลาทำงาน: workDuration(row.checkedInAt, row.checkedOutAt),
    }));
    downloadCsv(rows, `demo-attendance-${result.data.workDate}.csv`);
    toast.success("ดาวน์โหลดไฟล์ CSV สำหรับ Excel เรียบร้อยแล้ว");
  };

  const saveSettings = () =>
    savePayrollSettings.mutate({ idToken: idToken!, ...settingsDraft });
  const saveSalary = () =>
    saveSalaryProfile.mutate({
      idToken: idToken!,
      displayName: salaryDraft.displayName,
      monthlySalary: salaryDraft.monthlySalary ? Number(salaryDraft.monthlySalary) : null,
      dailyRate: salaryDraft.dailyRate ? Number(salaryDraft.dailyRate) : null,
      hourlyRate: salaryDraft.hourlyRate ? Number(salaryDraft.hourlyRate) : null,
      effectiveFrom: salaryDraft.effectiveFrom || null,
    });
  const saveEmployee = () =>
    saveManagedEmployee.mutate({
      idToken: idToken!,
      id: employeeDraft.id,
      displayName: employeeDraft.displayName,
      employeeCode: employeeDraft.employeeCode || null,
      department: employeeDraft.department || null,
    });

  const moduleMascot = (() => {
    const isMy = language === "my";
    const content: Record<
      TabType,
      { image: string; eyebrow: string; title: string; detail: string }
    > = {
      attendance: {
        image: "/demo-mascot-attendance.png",
        eyebrow: "SMART ATTENDANCE",
        title: isMy ? "စက္ကန့်ပိုင်းအတွင်း ယုံကြည်စိတ်ချစွာ အချိန်မှတ်တမ်းတင်ပါ" : "ลงเวลาอย่างมั่นใจในไม่กี่วินาที",
        detail: isMy ? "မိမိကိုယ်ကို အတည်ပြုခြင်း၊ အချိန်နှင့် တည်နေရာ အလိုအလျောက် စစ်ဆေးခြင်း" : "ตรวจสอบตัวตน เวลา และพิกัดจริงอัตโนมัติ",
      },
      leave: {
        image: "/demo-mascot-leave.png",
        eyebrow: "LEAVE MANAGEMENT",
        title: isMy ? "ခွင့်ရက်နှင့် လုပ်ငန်းလွှဲပြောင်းမှုများကို တစ်နေရာတည်းတွင် စီမံပါ" : "จัดการวันลาและงานส่งต่อในที่เดียว",
        detail: isMy ? "ခွင့်ခွင့်ပြုချက်ကြည့်ရန်၊ လျှောက်ထားရန်နှင့် အခြေအနေ စောင့်ကြည့်ရန်" : "ดูโควตา ยื่นคำขอ และติดตามผลได้ทันที",
      },
      "my-pulse": {
        image: "/demo-mascot-analytics.png",
        eyebrow: "MY PERFORMANCE PULSE",
        title: isMy ? "သင်၏ လုပ်ငန်းစွမ်းဆောင်ရည် မှတ်တမ်းများကို လွယ်ကူစွာ ကြည့်ရှုနိုင်ပါသည်" : "ทุกสถิติการทำงานของคุณ อ่านง่ายและตรวจสอบได้",
        detail: isMy ? "အလုပ်တက်/ဆင်းချိန်၊ အချိန်ပို (OT) နှင့် လစဉ်မှတ်တမ်းများ" : "เวลาเข้าออก OT และประวัติรายเดือน",
      },
      "leave-approvals": {
        image: "/demo-mascot-leave.png",
        eyebrow: "LEAVE APPROVAL",
        title: isMy ? "ခွင့်လျှောက်ထားချက်များကို စနစ်တကျ စိစစ်ပါ" : "พิจารณาคำขอวันลาอย่างเป็นระบบ",
        detail: isMy ? "ခွင့်ရက်အပိုင်းအခြား၊ အကြောင်းပြချက်နှင့် လုပ်ငန်းလွှဲပြောင်းလက်ခံသူကို စစ်ဆေးနိုင်သည်" : "เห็นช่วงลา เหตุผล และผู้รับมอบงานครบถ้วน",
      },
      "live-board": {
        image: "/demo-mascot-executive.png",
        eyebrow: "EXECUTIVE LIVE BOARD",
        title: isMy ? "ယနေ့ ဆုံးဖြတ်ချက်ချမှတ်ရန် ဝန်ထမ်းအင်အား အလုံးစုံမြင်ကွင်း" : "ภาพรวมกำลังคนสำหรับการตัดสินใจวันนี้",
        detail: isMy ? "တစ်ဖွဲ့လုံး၏ အလုပ်တက်/ဆင်းချိန်နှင့် အခြေအနေကို အချိန်နှင့်တပြေးညီ ကြည့်ရှုနိုင်သည်" : "ติดตามเวลาเข้าออกและสถานะทั้งองค์กรแบบเรียลไทม์",
      },
      payroll: {
        image: "/demo-mascot-analytics.png",
        eyebrow: "PAYROLL & ANALYTICS",
        title: isMy ? "လစာ၊ အချိန်ပို (OT) နှင့် အရေးကြီး အချက်အလက်များ အကျဉ်းချုပ်" : "สรุปเงินเดือน OT และข้อมูลสำคัญในมุมเดียว",
        detail: isMy ? "စီမံခန့်ခွဲသူများအတွက် အစီရင်ခံစာများ စစ်ဆေးပြီး ထုတ်ယူနိုင်ပါသည်" : "พร้อมตรวจสอบและส่งออกรายงานสำหรับฝ่ายบริหาร",
      },
      "team-directory": {
        image: "/demo-mascot-executive.png",
        eyebrow: "TEAM DIRECTORY",
        title: isMy ? "စနစ်တကျရှိပြီး ယုံကြည်စိတ်ချရသော ဝန်ထမ်းအချက်အလက်များ" : "ข้อมูลบุคลากรที่เป็นระเบียบและเชื่อถือได้",
        detail: isMy ? "အမည်စာရင်း၊ ဝန်ထမ်းကုဒ်နှင့် ဌာနများကို စီမံခန့်ခွဲရန်" : "จัดการรายชื่อ รหัสพนักงาน และแผนก",
      },
      "hr-settings": {
        image: "/demo-mascot-executive.png",
        eyebrow: "HR CONTROL CENTER",
        title: isMy ? "HR စနစ် ဆက်တင်များကို ဗဟိုမှ ထိန်းချုပ်ပါ" : "ตั้งค่าระบบงานบุคคลจากศูนย์กลาง",
        detail: isMy ? "လစာတွက်ချက်မှုကာလ၊ အခွင့်အရေးများနှင့် အဖွဲ့အချက်အလက်များကို ထိန်းချုပ်ရန်" : "ควบคุมรอบเงินเดือน สิทธิ์ และข้อมูลทีม",
      },
      "admin-team": {
        image: "/demo-mascot-executive.png",
        eyebrow: "ADMINISTRATION",
        title: isMy ? "တူညီသော အချက်အလက်များဖြင့် အဖွဲ့ကို စီမံပါ" : "บริหารทีมด้วยข้อมูลชุดเดียวกัน",
        detail: isMy ? "ထပ်နေသော အချက်အလက်များကို လျှော့ချပြီး မော်ဂျူးအားလုံး ချိတ်ဆက်စေပါသည်" : "ลดข้อมูลซ้ำและทำให้ทุกโมดูลสอดคล้องกัน",
      },
      "salary-advance": {
        image: "/demo-mascot-analytics.png",
        eyebrow: "SALARY ADVANCE",
        title: isMy ? "အမှန်တကယ် လုပ်ကိုင်ထားသော အခွင့်အရေးအရ လစာကြိုတင်ထုတ်ယူပါ" : "ขอเบิกเงินล่วงหน้าตามสิทธิ์ทำงานจริง",
        detail: isMy ? "လက်ရှိလည်ပတ်မှုအတွင်း လုပ်ကိုินထားသော လုပ်ခလစာထက် မကျော်လွန်စေဘဲ ထုတ်ယူနိုင်ပါသည် (၁၀ ရက် နှင့် ၂၅ ရက် ဖြတ်တောက်)" : "เบิกได้ไม่เกินค่าแรงที่ทำงานแล้วในรอบปัจจุบัน (ตัดรอบ 10 และ 25)",
      },
      "advance-approvals": {
        image: "/demo-mascot-executive.png",
        eyebrow: "SALARY ADVANCE APPROVAL",
        title: isMy ? "လစာကြိုတင်ထုတ်ယူခွင့် လျှောက်ထားချက်များကို စိစစ်ပါ" : "พิจารณาคำขอเบิกเงินล่วงหน้า",
        detail: isMy ? "အမှန်တကယ် လုပ်ကိုင်သည့်ရက်၊ ကျန်ရှိသော အခွင့်အရေးများကို စစ်ဆေးပြီး အတည်ပြု/ငြင်းပယ်ပါ" : "ตรวจสอบวันทำงานจริง สิทธิ์คงเหลือ และอนุมัติ/ไม่อนุมัติ",
      },
      tasks: {
        image: "/demo-mascot-executive.png",
        eyebrow: "TASK & OT DISPATCH",
        title: isMy ? "လုပ်ငန်းတာဝန်နှင့် အချိန်ပို (OT) စေခိုင်းမှုစနစ်" : "ระบบสั่งงานและงานล่วงเวลา",
        detail: isMy ? "အချိန်၊ နေရာ၊ ဝန်ထမ်းတစ်ဦးချင်း သတ်မှတ်ပြီး LINE မှတစ်ဆင့် အကြောင်းကြားပါ" : "กำหนดเวลา สถานที่ รายบุคคล พร้อมแจ้งเตือนเข้า LINE",
      },
      welfare: {
        image: "/demo-mascot-leave.png",
        eyebrow: "EMPLOYEE WELFARE",
        title: isMy ? "ဝန်ထမ်း သက်သာချောင်ချိရေး စနစ်" : "ระบบสวัสดิการพนักงาน",
        detail: isMy ? "သက်သာချောင်ချိရေး လျှောက်ထားပြီး အတည်ပြုချက် အခြေအနေ စစ်ဆေးပါ" : "ยื่นคำขอเบิกสวัสดิการและตรวจสอบผลการอนุมัติ",
      },
    };
    return content[activeTab];
  })();

  return (
    <div
      className={`app-shell demo-dashboard min-h-screen text-slate-900 overflow-x-hidden ${
        executiveEntry ? "executive-theme pb-6" : "employee-theme employee-compact pb-2"
      }`}
    >
      <div className="isometric-grid pointer-events-none fixed inset-0 opacity-30" />
      <div className="ambient-orb orb-teal" />
      <div className="ambient-orb orb-coral" />
      <div className="ambient-orb orb-blue" />

      <HomeHeader
        currentDisplayName={currentDisplayName}
        savedName={savedName}
        liffState={liffState}
        liffError={liffError}
        isManager={isManager}
        executiveEntry={executiveEntry}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        inputName={inputName}
        setInputName={setInputName}
        candidateNames={candidateNames}
        handleSaveFirstTimeName={handleSaveFirstTimeName}
        handleClearSavedUser={handleClearSavedUser}
        moduleMascot={moduleMascot}
        showFirstTimeModal={showFirstTimeModal}
        entryModule={entryModule}
        featureFlags={featureFlags}
      />

      <main className={`dashboard-container mx-auto px-3 sm:px-5 ${executiveEntry ? "dashboard-container-admin" : "dashboard-container-employee"}`}>
        {activeTab === "attendance" && (
          <AttendanceTab
            currentDisplayName={currentDisplayName}
            now={now}
            status={status}
            today={today}
            canCheckIn={canCheckIn}
            canCheckout={canCheckout}
            checkIn={checkIn}
            checkout={checkout}
            idToken={idToken ?? null}
            setConfirmAction={setConfirmAction}
            setActiveTab={setActiveTab}
            myPerformance={myPerformance}
            myMonthlySummary={myMonthlySummary}
            performanceComments={performanceComments}
            setPerformanceComments={setPerformanceComments}
            commentPerformance={commentPerformance}
            featureFlags={featureFlags}
            candidateNames={candidateNames}
            handleSaveFirstTimeName={handleSaveFirstTimeName}
          />
        )}

        {activeTab === "my-pulse" && <MyPulseTab myMonthlySummary={myMonthlySummary} />}

        {featureFlags.enableLeaveSystem && activeTab === "leave" && (
          <LeaveTab
            leaveDashboard={leaveDashboard}
            leaveDraft={leaveDraft}
            setLeaveDraft={setLeaveDraft}
            createLeave={createLeave}
            cancelLeave={cancelLeave}
            idToken={idToken ?? null}
            featureFlags={featureFlags}
          />
        )}

        {featureFlags.enableLeaveSystem && executiveEntry && isManager && activeTab === "leave-approvals" && (
          <LeaveApprovalsTab
            leaveAdmin={leaveAdmin}
            decideLeave={decideLeave}
            idToken={idToken ?? null}
          />
        )}

        {executiveEntry && isManager && activeTab === "live-board" && (
          <LiveBoardTab
            sheetMonth={sheetMonth}
            setSheetMonth={setSheetMonth}
            sheetDashboard={sheetDashboard}
            dashboard={dashboard}
            downloadExcel={downloadExcel}
            exportAttendance={exportAttendance}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            query={query}
            setQuery={setQuery}
            filtered={filtered}
            idToken={idToken ?? null}
            systemConfigs={systemConfigs?.data}
          />
        )}

        {executiveEntry && isManager && activeTab === "payroll" && (
          <PayrollTab
            payrollSummary={payrollSummary}
            downloadExcel={downloadExcel}
            exportAttendance={exportAttendance}
            idToken={idToken ?? null}
          />
        )}

        {executiveEntry && isManager && featureFlags.enableSalaryAdvance && activeTab === "advance-approvals" && (
          <AdvanceApprovalsTab idToken={idToken ?? null} />
        )}

        {featureFlags.enableSalaryAdvance && activeTab === "salary-advance" && (
          <SalaryAdvanceTab idToken={idToken ?? null} />
        )}

        {executiveEntry && isManager && activeTab === "team-directory" && (
          <TeamDirectoryTab
            managedEmployees={managedEmployees}
            employeeDraft={employeeDraft}
            setEmployeeDraft={setEmployeeDraft}
            saveEmployee={saveEmployee}
            saveManagedEmployee={saveManagedEmployee}
            setManagedEmployeeActive={setManagedEmployeeActive}
            deleteManagedEmployee={deleteManagedEmployee}
            hiddenEmployees={hiddenEmployees}
            visibleSourceNames={visibleSourceNames}
            restoreEmployee={restoreEmployee}
            hideEmployee={hideEmployee}
            idToken={idToken ?? null}
          />
        )}

        {executiveEntry && isManager && activeTab === "hr-settings" && (
          <HrSettingsTab
            performanceAdmin={performanceAdmin}
            performanceDraft={performanceDraft}
            setPerformanceDraft={setPerformanceDraft}
            savePerformance={savePerformance}
            payrollSettings={payrollSettings}
            settingsDraft={settingsDraft}
            setSettingsDraft={setSettingsDraft}
            saveSettings={saveSettings}
            savePayrollSettings={savePayrollSettings}
            salaryDraft={salaryDraft}
            setSalaryDraft={setSalaryDraft}
            saveSalary={saveSalary}
            saveSalaryProfile={saveSalaryProfile}
            payrollProfiles={payrollProfiles}
            idToken={idToken ?? null}
            systemConfigs={systemConfigs.data}
            updateSystemConfig={updateSystemConfig}
          />
        )}

        {activeTab === "tasks" && (
          <TaskDispatchTab
            idToken={idToken ?? null}
            isManager={executiveEntry && isManager}
            employees={managedEmployees.data || []}
          />
        )}

        {activeTab === "welfare" && (
          <WelfareTab
            idToken={idToken ?? null}
            isManager={executiveEntry && isManager}
          />
        )}

        <HomeModals
          confirmAction={confirmAction}
          confirmCopy={confirmCopy}
          setConfirmAction={setConfirmAction}
          runConfirmedAction={runConfirmedAction}
          checkInSuccess={checkInSuccess}
          lateReasonRequest={lateReasonRequest}
          submitLateReason={(reason) => idToken && lateReasonRequest && submitLateReason.mutate({ idToken, attendanceId: lateReasonRequest.attendanceId, reason })}
          lateReasonPending={submitLateReason.isPending}
        />
      </main>
    </div>
  );
}
